import React from 'react';
import { StepFilesProps } from './StepFiles';
import StepFiles from './StepFiles';

export interface StepVersionFilesProps extends StepFilesProps {
  description: string;
  onDescriptionChange: (v: string) => void;
  descriptionError: string;
  dataVersion: string;
  onDataVersionChange: (v: string) => void;
  dataVersionError: string;
  gdsEnabled: boolean;
  gdsLocked?: boolean;
  hasGdsLicense?: boolean;
  onGdsToggle: () => void;
  gdsError: string;
}

const StepVersionFiles: React.FC<StepVersionFilesProps> = ({
  description, onDescriptionChange, descriptionError,
  dataVersion, onDataVersionChange, dataVersionError,
  gdsEnabled, gdsLocked, hasGdsLicense, onGdsToggle, gdsError,
  // rest are StepFilesProps
  nodeFiles, edgeFiles, onRemoveNodeFile, onRemoveEdgeFile,
  onClearNodeFiles, onClearEdgeFiles, nodeFilesError, onAssignFiles,
  selectedWorkspace, workspaces, wsLoading, wsError, wsTotal, wsFrom, WS_PAGE,
  onWorkspaceSelect, onLoadWorkspaces,
  files, fileLoading, fileError, fileTotal, fileFrom, FILE_PAGE,
  breadcrumb, onFolderClick, onBreadcrumbClick, onLoadFiles, currentParentId,
  onSelectAll, selectingAll,
}) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>

      {/* Compact metadata row above the file browser */}
      <div style={{ display: 'flex', gap: 16, flexShrink: 0, alignItems: 'flex-start' }}>
        <div className="wiz-field" style={{ flex: 1, marginBottom: 0 }}>
          <label className="wiz-label">Description <span className="wiz-required">Required</span></label>
          <input
            className={`wiz-input${descriptionError ? ' wiz-input--error' : ''}`}
            type="text"
            value={description}
            onChange={e => onDescriptionChange(e.target.value)}
            placeholder="Version description"
            autoFocus
          />
          {descriptionError && <span className="wiz-field-error">{descriptionError}</span>}
        </div>

        <div className="wiz-field" style={{ flex: 1, marginBottom: 0 }}>
          <label className="wiz-label">Data version <span className="wiz-required">Required</span></label>
          <input
            className={`wiz-input${dataVersionError ? ' wiz-input--error' : ''}`}
            type="text"
            value={dataVersion}
            onChange={e => onDataVersionChange(e.target.value)}
            placeholder="e.g. v1.0.0"
            autoComplete="off"
          />
          {dataVersionError
            ? <span className="wiz-field-error">{dataVersionError}</span>
            : <span className="wiz-field-hint">Any string works — we suggest <strong>v1.0.0</strong> format</span>
          }
        </div>

        {/* GDS inline toggle */}
        <div className="wiz-field" style={{ flexShrink: 0, marginBottom: 0 }}>
          <label className="wiz-label" style={{ marginBottom: 8 }}>GDS</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 40 }}>
            <button
              type="button"
              role="switch"
              aria-checked={gdsEnabled}
              className={`wiz-switch${gdsEnabled ? ' wiz-switch--on' : ''}${(gdsLocked || !hasGdsLicense) ? ' wiz-switch--locked' : ''}`}
              onClick={onGdsToggle}
              disabled={gdsLocked || !hasGdsLicense}
              title={
                gdsLocked
                  ? 'GDS is required for all versions of this KG'
                  : !hasGdsLicense
                  ? 'Your account does not have GDS access. Contact your administrator to request POLLY_KG_GDS_ORG_ADMIN.'
                  : undefined
              }
            >
              <span className="wiz-switch-thumb" />
            </button>
            <span style={{ fontSize: 12, color: 'var(--muted-foreground)', fontFamily: 'var(--font-primary)', whiteSpace: 'nowrap' }}>
              {gdsEnabled ? 'Enabled' : 'Disabled'}
              {gdsLocked && <span style={{ marginLeft: 4, fontSize: 10, color: 'var(--primary)' }}>· required</span>}
            </span>
          </div>
          {gdsError && <span className="wiz-field-error" style={{ fontSize: 10 }}>{gdsError}</span>}
        </div>
      </div>

      {/* File browser fills remaining space */}
      <div style={{ flex: 1, minHeight: 0 }}>
        <StepFiles
          nodeFiles={nodeFiles}
          edgeFiles={edgeFiles}
          onRemoveNodeFile={onRemoveNodeFile}
          onRemoveEdgeFile={onRemoveEdgeFile}
          onClearNodeFiles={onClearNodeFiles}
          onClearEdgeFiles={onClearEdgeFiles}
          nodeFilesError={nodeFilesError}
          onAssignFiles={onAssignFiles}
          selectedWorkspace={selectedWorkspace}
          workspaces={workspaces}
          wsLoading={wsLoading}
          wsError={wsError}
          wsTotal={wsTotal}
          wsFrom={wsFrom}
          WS_PAGE={WS_PAGE}
          onWorkspaceSelect={onWorkspaceSelect}
          onLoadWorkspaces={onLoadWorkspaces}
          files={files}
          fileLoading={fileLoading}
          fileError={fileError}
          fileTotal={fileTotal}
          fileFrom={fileFrom}
          FILE_PAGE={FILE_PAGE}
          breadcrumb={breadcrumb}
          onFolderClick={onFolderClick}
          onBreadcrumbClick={onBreadcrumbClick}
          onLoadFiles={onLoadFiles}
          currentParentId={currentParentId}
          onSelectAll={onSelectAll}
          selectingAll={selectingAll}
        />
      </div>
    </div>
  );
};

export default StepVersionFiles;
